import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, Phone, ChevronDown, ShieldCheck } from "lucide-react";
import { toast } from "react-toastify";
import { register, sendOtp } from "../../services/AuthService";

export default function SignupForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    userType: "",
    password: "",
    confirmPassword: "",
  });

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validatePhone = (phone) => {
    const normalizedPhone = String(phone || "").replace(/\s+/g, "");
    return /^(\+84|0)\d{9,10}$/.test(normalizedPhone);
  };

  const validate = () => {
    if (!formData.email) {
      toast.error("Vui lòng điền thông tin email.");
      return false;
    }

    if (!validateEmail(formData.email)) {
      toast.error("Email không đúng định dạng.");
      return false;
    }

    if (!formData.fullName) {
      toast.error("Vui lòng điền thông tin họ và tên.");
      return false;
    }

    if (!formData.password) {
      toast.error("Vui lòng điền thông tin mật khẩu.");
      return false;
    }

    if (!formData.phone) {
      toast.error("Vui lòng điền thông tin số điện thoại.");
      return false;
    }

    if (!validatePhone(formData.phone)) {
      toast.error("Số điện thoại không đúng định dạng.");
      return false;
    }

    if (!formData.userType) {
      toast.error("Vui lòng chọn đối tượng học.");
      return false;
    }

    if (!validatePassword(formData.password)) {
      toast.error("Mật khẩu có độ dài ít nhất 6 kí tự.");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Xác nhận mật khẩu không khớp.");
      return false;
    }
    return true;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await sendOtp(formData.phone);
      setOtpSent(true);
      setResendCount(0);
      toast.success("Mã OTP đã được gửi đến Zalo của bạn!");
    } catch (error) {
      const message = error?.response?.data?.message || "Gửi OTP thất bại, vui lòng thử lại.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCount >= 2) return;
    try {
      setLoading(true);
      await sendOtp(formData.phone);
      setResendCount((c) => c + 1);
      toast.success("Đã gửi lại mã OTP!");
    } catch (error) {
      const data = error?.response?.data;
      if (data?.resendLimitReached) {
        setResendCount(2);
      }
      const message = data?.message || "Gửi lại OTP thất bại.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      toast.error("Vui lòng nhập mã OTP.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        userType: formData.userType,
        password: formData.password,
        otpCode: otpCode.trim(),
      };
      const response = await register(payload);
      if (response && response.message) {
        toast.success(response.message);
        navigate("/login");
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link
            to="/"
            className="group flex items-center gap-3 mb-8 hover:opacity-90 transition-opacity"
          >
            <div className="flex items-center justify-center w-72 h-20 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 shadow-md group-hover:shadow-lg transition-all">
              <img
                src="/logo.png"
                alt="86HSK Logo"
                className="w-68 h-20 object-contain"
              />
            </div>
          </Link>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              Bắt đầu hành trình
            </h1>
            <p className="text-gray-600">
              Tạo tài khoản để khám phá hàng nghìn khóa học chất lượng
            </p>
          </div>

          {!otpSent ? (
            /* ── Bước 1: Điền thông tin ── */
            <form onSubmit={handleSendOtp} className="space-y-5">
              {/* Full Name Input */}
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Họ và tên
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="Nguyễn Văn A"
                    required
                  />
                </div>
              </div>

              {/* Phone Input */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Số điện thoại
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="VD: 0987654321"
                    required
                  />
                </div>
              </div>

              {/* User Type Input */}
              <div>
                <label
                  htmlFor="userType"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Bạn là
                </label>
                <div className="relative">
                  <select
                    id="userType"
                    value={formData.userType}
                    onChange={(e) => handleInputChange("userType", e.target.value)}
                    className="block w-full pl-4 pr-10 py-3 border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all appearance-none bg-white"
                    required
                  >
                    <option value="">Chọn đối tượng học</option>
                    <option value="Học sinh">Học sinh</option>
                    <option value="Sinh viên">Sinh viên</option>
                    <option value="Người đi làm">Người đi làm</option>
                  </select>
                  <ChevronDown className="h-4 w-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="name@example.com"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mật khẩu
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="Tối thiểu 6 ký tự"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Xác nhận mật khẩu
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                    placeholder="Nhập lại mật khẩu"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Send OTP Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Đang gửi OTP..." : "Tiếp theo — Gửi mã OTP"}
              </button>
            </form>
          ) : (
            /* ── Bước 2: Nhập OTP ── */
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* OTP info banner */}
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  Mã OTP đã được gửi qua <strong>Zalo</strong> đến số{" "}
                  <strong>{formData.phone}</strong>. Mã có hiệu lực trong{" "}
                  <strong>5 phút</strong>.
                </p>
              </div>

              {/* OTP Input */}
              <div>
                <label
                  htmlFor="otpCode"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Mã OTP
                </label>
                <input
                  id="otpCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  className="block w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 text-center text-2xl tracking-widest placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="• • • • • •"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Đang xử lý..." : "Đăng ký"}
              </button>

              {/* Resend + Back */}
              <div className="flex items-center justify-between text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => { setOtpSent(false); setOtpCode(""); }}
                  className="hover:text-gray-900 underline cursor-pointer"
                >
                  Sửa thông tin
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading || resendCount >= 2}
                  className="text-red-600 hover:text-red-700 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendCount >= 2 ? "Đã gửi lại tối đa (2/2)" : `Gửi lại OTP${resendCount > 0 ? ` (${resendCount}/2)` : ""}`}
                </button>
              </div>
            </form>
          )}

          {/* Login Link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            Đã có tài khoản?{" "}
            <Link
              to="/login"
              className="font-semibold text-red-600 hover:text-red-700 cursor-pointer"
            >
              Đăng nhập ngay
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image/Brand */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-red-600 to-red-700 items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="whitespace-nowrap text-4xl font-bold mb-6">
            Gia nhập cùng 86 HSK
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Hàng ngàn học viên đã tin tưởng và thành công cùng 86 HSK
          </p>
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-1">98%</div>
              <p className="text-red-100">Tỷ lệ hài lòng của học viên</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl font-bold mb-1">10,000+</div>
              <p className="text-red-100">Học viên đang học tập</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

